'use client';
import { useRef, useCallback, useEffect } from 'react';
import { createAriaHider } from './createAriaHider.js';
import { scopeTab } from './scopeTab.js';
import { FOCUS_SELECTOR, tabbable, focusable } from './tabbable.js';

const useFocusTrap = (active = true)=>{
    const ref = useRef();
    const restoreAria = useRef(null);
    const focusNode = (node)=>{
        let focusElement = node.querySelector('[data-autofocus]');
        if (!focusElement) {
            const children = Array.from(node.querySelectorAll(FOCUS_SELECTOR));
            focusElement = children.find(tabbable) || children.find(focusable) || null;
            if (!focusElement && focusable(node)) {
                focusElement = node;
            }
        }
        if (focusElement) {
            focusElement.focus({
                preventScroll: true
            });
        }
    };
    const setRef = useCallback((node)=>{
        if (!active) {
            return;
        }
        if (node === null) {
            if (restoreAria.current) {
                restoreAria.current();
                restoreAria.current = null;
            }
            return;
        }
        restoreAria.current = createAriaHider(node);
        if (ref.current === node) {
            return;
        }
        if (node) {
            /*
                 * Откладываем обработку узла на один frame.
                 * Это гарантирует корректное добавление фокуса.
                 * */ setTimeout(()=>{
                if (node.getRootNode()) {
                    focusNode(node);
                }
            });
            ref.current = node;
        } else {
            ref.current = null;
        }
    }, [
        active
    ]);
    useEffect(()=>{
        if (!active) {
            return undefined;
        }
        if (ref.current) {
            setTimeout(()=>focusNode(ref.current));
        }
        const handleKeyDown = (event)=>{
            if (event.key === 'Tab' && ref.current) {
                scopeTab(ref.current, event);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return ()=>{
            document.removeEventListener('keydown', handleKeyDown);
            if (restoreAria.current) {
                restoreAria.current();
            }
        };
    }, [
        active
    ]);
    return setRef;
};

export { useFocusTrap };
//# sourceMappingURL=useFocusTrap.js.map
